import { binaryInsert, binarySearch } from "../collection.array.tools";

/** Sorted map entry */
export interface SortedChainedMapEntry<K, V> {
    prev?: SortedChainedMapEntry<K, V>;
    next?: SortedChainedMapEntry<K, V>;

    readonly key: K;
    value: V;
}

/**
 * Utility class to store sorted key,value pairs.
 * Data is stored as an entry containing its predecessor and successor to allow quick navigation through items.
 */
export class SortedChainedMap<K, V> {
    protected readonly _sortedEntries: SortedChainedMapEntry<K, V>[] = [];
    protected readonly _existingValues: Map<K, SortedChainedMapEntry<K, V>> = new Map();

    protected readonly _keyComparator: (a: K, b: K) => number;
    protected readonly _entryComparator: (a: SortedChainedMapEntry<K, V>, b: SortedChainedMapEntry<K, V>) => number;

    constructor(protected _options: {
        /** Custom comparator */
        comparator?: (a: K, b: K) => number,
        buildDefaultValue: (key: K, prev?: SortedChainedMapEntry<K, V>, next?: SortedChainedMapEntry<K, V>) => V,
        cloneValue: (value: V) => V
    }) {
        this._keyComparator = this._options.comparator ?? function (a, b) { return +a - +b; };
        this._entryComparator = (a, b) => { return this._keyComparator(a.key, b.key); };
    }

    //#region Utility getters -------------------------------------------------

    /** Get the lowest value */
    public get front(): SortedChainedMapEntry<K, V> | undefined {
        return this._sortedEntries.length > 0 ? this._sortedEntries[0] : undefined;
    }

    /** Get the highest value */
    public get back(): SortedChainedMapEntry<K, V> | undefined {
        return this._sortedEntries.length > 0 ? this._sortedEntries[this._sortedEntries.length - 1] : undefined;
    }

    /** Get the count of entries */
    public get length(): number {
        return this._sortedEntries.length;
    }

    /** Get the list of entries in sorted order */
    public get entries(): SortedChainedMapEntry<K, V>[] {
        return [...this._sortedEntries];
    }

    /** 
     * Get the list of values in the order of their keys.
     * Does not remove any duplicate value.
     */
    public get sortedValues(): V[] {
        const result: V[] = [];
        for (const entry of this._sortedEntries) {
            result.push(entry.value);
        }
        return result;
    }

    //#endregion

    //#region Manipulation of the list ----------------------------------------

    /** Clear the list */
    public clear(): void {
        // Help disposal of items
        for (const entry of this._sortedEntries) {
            entry.prev = undefined;
            entry.next = undefined;
        }
        this._sortedEntries.splice(0, this._sortedEntries.length); // Here we splice to keep array readonly
        this._existingValues.clear();
    }

    /** 
     * Insert a pair (key,value) in the current map at the correct position in the chained list.
     * If the Entry already exists, the value is replaced.
     * @returns The entry found or newly created.
     * 
     * MAB: J'avais pensé permettre de passer l'entry à partir de laquelle rechercher, 
     * mais de toute façon il faudrait la trouver dans le tableau ... donc l'optimisation serait négligeable, voir contre-productive.
     */
    public insert(key: K, value: V): SortedChainedMapEntry<K, V> {
        const existingEntry = this._existingValues.get(key);
        if (existingEntry != null) {
            // Key is already existing, update value
            existingEntry.value = value;
            return existingEntry;
        } else {
            const newEntry: SortedChainedMapEntry<K, V> = {
                key,
                value
            };
            const insertionIndex = binaryInsert(this._sortedEntries, newEntry, false, this._entryComparator.bind(this));
            if (insertionIndex > 0) {
                // Chain with previous item
                newEntry.prev = this._sortedEntries[insertionIndex - 1];
                newEntry.prev.next = newEntry;
            }
            if (insertionIndex < this._sortedEntries.length - 1) {
                // Chain with next item
                newEntry.next = this._sortedEntries[insertionIndex + 1];
                newEntry.next.prev = newEntry;
            }
            this._existingValues.set(key, newEntry);
            return newEntry;
        }
    }

    /** @returns the entry with its key equal to or lower than passed key */
    public getFloorEntry(key: K): SortedChainedMapEntry<K, V> | null {
        const existingEntry = this._existingValues.get(key);
        if (existingEntry != null) {
            return existingEntry;
        }
        const targetEntry: SortedChainedMapEntry<K, V> = { key } as SortedChainedMapEntry<K, V>;

        if (this._sortedEntries.length === 0) {
            return null;
        }
        let index = binarySearch(this._sortedEntries, targetEntry, this._entryComparator.bind(this));
        if (index >= 0) {
            return this._sortedEntries[index - 1];
        }
        index = ~index;
        if (index > 0) {
            return this._sortedEntries[index - 1];
        } else {
            return null;
        }
    }

    /** @returns the entry with its key equal to or higher than passed key */
    public getCeilEntry(key: K): SortedChainedMapEntry<K, V> | null {
        if (this._sortedEntries.length === 0) {
            return null;
        }
        const floorEntry = this.getFloorEntry(key);
        if (floorEntry == null) {
            // This is not because the list is empty, we already tested that before
            return this.front ?? null;
        } else if (this._keyComparator(floorEntry.key, key) === 0) {
            // Exact match, keep it
            return floorEntry;
        } else {
            // Lower key, take the next entry
            return floorEntry?.next ?? null;
        }
    }

    /** @returns the entry with its key lower than passed key */
    public getPrevEntry(key: K): SortedChainedMapEntry<K, V> | null {
        if (this._sortedEntries.length === 0) {
            return null;
        }
        const floorEntry = this.getFloorEntry(key);
        if (floorEntry != null && this._keyComparator(floorEntry.key, key) === 0) {
            // Exact match, return the previous entry
            return floorEntry?.prev ?? null;
        } else {
            // We are either on null or on a lower key
            return floorEntry;
        }
    }

    /** @returns the entry with its key higher than passed key */
    public getNextEntry(key: K): SortedChainedMapEntry<K, V> | null {
        if (this._sortedEntries.length === 0) {
            return null;
        }
        const floorEntry = this.getFloorEntry(key);
        if (floorEntry == null) {
            // This is not because the list is empty, we already tested that before,
            // so we need to get the first entry
            return this.front ?? null;
        } else {
            return floorEntry?.next ?? null;
        }
    }

    /** @returns Existing entry or newly created entry */
    public getOrCreateEntry(key: K): SortedChainedMapEntry<K, V> {
        const existingEntry = this._existingValues.get(key);
        if (existingEntry != null) {
            return existingEntry;
        }
        else {
            const entry = this.insert(key, this._options.buildDefaultValue(key));
            return entry;
        }
    }

    //#endregion

    //#region Clone -----------------------------------------------------------

    /** 
     * @returns A cloned version of the instance. 
     * Keys and values are not cloned.
     */
    public clone(): SortedChainedMap<K, V> {
        const cloned = new SortedChainedMap<K, V>(this._options);
        let prevCloned: SortedChainedMapEntry<K, V> | undefined = undefined;
        for (const entry of this._sortedEntries) {
            const clonedEntry: SortedChainedMapEntry<K, V> = {
                key: entry.key,
                value: this._options.cloneValue(entry.value),
                prev: prevCloned,
                next: undefined
            };
            if (prevCloned != null) {
                prevCloned.next = clonedEntry;
            }
            cloned._sortedEntries.push(clonedEntry);
            cloned._existingValues.set(clonedEntry.key, clonedEntry);
            prevCloned = clonedEntry;
        }
        return cloned;
    }

    //#endregion

    //#region Filtering -------------------------------------------------------

    /** 
     * Rechain the list of entries.
     * 
     * The passed callback must either return :
     * - undefined to delete entry
     * - modified entry (key, value, next) to update the chain
     * There is no need to modify the entry, its successor nor its predecessor, they will be updated by the rechain() function.
     */
    public filter(f: (entry: Omit<SortedChainedMapEntry<K, V>, "next" | "prev">, previous?: Omit<SortedChainedMapEntry<K, V>, "next" | "prev">) => boolean): void {
        if (this._sortedEntries.length === 0) {
            // Quick exit
            return;
        }

        for (let i = 0; i < this._sortedEntries.length;) {
            const previous = i <= 0 ? undefined : this._sortedEntries[i - 1];
            const current = this._sortedEntries[i];
            const keep = f(current, previous);
            if (keep) {
                // Keep the entry
                i++;
            } else {
                // Delete the entry
                const next = i < this._sortedEntries.length - 1 ? this._sortedEntries[i + 1] : undefined;
                if (previous) {
                    // Rechain the previous
                    previous.next = next;
                    // Rechain the next
                }
                if (next) {
                    next.prev = previous;
                }
                // Really delete the entry from the storage
                current.prev = undefined;
                current.next = undefined;
                this._existingValues.delete(current.key);
                this._sortedEntries.splice(i, 1);
            }
        }
    }

    /** @throws If consistency is not correct */
    public checkConsistency(): void {
        if (this._existingValues.size !== this._sortedEntries.length) {
            throw "Invalid state";
        }
        for (const [key, entry] of this._existingValues.entries()) {
            if (key !== entry.key) {
                throw "Invalid state";
            }
        }
        for (let i = 0; i < this._sortedEntries.length; i++) {
            const entry = this._sortedEntries[i];
            if (i > 0) {
                const prevEntry = this._sortedEntries[i - 1];
                if (prevEntry.next !== entry) {
                    throw "Invalid state";
                }
                if (entry.prev !== prevEntry) {
                    throw "Invalid state";
                }
            }
            if (i < this._sortedEntries.length - 1) {
                const nextEntry = this._sortedEntries[i + 1];
                if (entry.next !== nextEntry) {
                    throw "Invalid state";
                }
                if (entry !== nextEntry.prev) {
                    throw "Invalid state";
                }
            }
        }
    }
}
